/**
 * EditHistoryPanel — Side-drawer view of admin report edits.
 *
 * Groups edits by edit session (one session = the time between a
 * "Muokkaa raporttia" toggle on → off). Each session lists the editor,
 * timestamp range, and the fields they touched. Pre-093 edits with no
 * session_id fall into one synthetic "legacy" bucket per editor.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { diffWords } from 'diff';
import { ChevronDown, ChevronRight, RotateCcw, User, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { useReportEdits } from './ReportEditsContext';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(180deg, #0a0a1a 0%, #111128 100%);
  color: white;
`;

const Banner = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Subtitle = styled.div`
  flex: 1;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.4;
`;

const RefreshBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
  svg { ${p => p.$loading ? 'animation: spin 1s linear infinite;' : ''} }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ErrorBox = styled.div`
  margin: 12px 16px;
  padding: 10px 12px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #fecaca;
  font-size: 0.78rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Body = styled.div`
  padding: 12px 16px;
`;

const SessionCard = styled.div`
  margin-bottom: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  overflow: hidden;
`;

const SessionHead = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  &:hover { background: rgba(255, 255, 255, 0.04); }
`;

const Chevron = styled.span`
  color: rgba(255, 255, 255, 0.3);
  display: flex;
`;

const SessionMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const Editor = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionTime = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
`;

const Count = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.06);
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
`;

const LegacyTag = styled.span`
  font-size: 0.65rem;
  color: rgba(245, 158, 11, 0.9);
  background: rgba(245, 158, 11, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 6px;
`;

const EditList = styled.div`
  padding: 0 12px 10px;
`;

const EditRow = styled.div`
  padding: 8px 10px;
  margin-top: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldKey = styled.div`
  font-size: 0.65rem;
  font-family: 'JetBrains Mono', monospace;
  color: rgba(255, 255, 255, 0.4);
  word-break: break-all;
`;

const EditText = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
`;

/* Word-diff styling — same blue as the AI/human segment highlight
 * (SegmentedText.jsx) so the UX feels consistent across the report. */
const AddedSpan = styled.span`
  color: #7AB8E8;
  background: rgba(122, 184, 232, 0.12);
  border-radius: 2px;
  padding: 0 1px;
`;

const RemovedSpan = styled.span`
  color: rgba(239, 68, 68, 0.75);
  background: rgba(239, 68, 68, 0.08);
  border-radius: 2px;
  padding: 0 1px;
  text-decoration: line-through;
`;

function DiffText({ current, previous }) {
  if (!previous || previous === current) return <>{current}</>;
  const parts = diffWords(previous, current);
  return (
    <>
      {parts.map((p, i) => {
        if (p.added) return <AddedSpan key={i}>{p.value}</AddedSpan>;
        if (p.removed) return <RemovedSpan key={i}>{p.value}</RemovedSpan>;
        return <span key={i}>{p.value}</span>;
      })}
    </>
  );
}

const RestoreBtn = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(168, 85, 247, 0.9);
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-family: inherit;
  &:hover { background: rgba(168, 85, 247, 0.18); color: #fff; }
  &:disabled { opacity: 0.5; cursor: progress; }
`;

const Empty = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
`;

const LoadingBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.4);
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fi-FI', {
      day: 'numeric', month: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRange(start, end) {
  const s = formatTime(start);
  const e = formatTime(end);
  if (!s || s === e) return s;
  return `${s} – ${e.split(' ').pop()}`;
}

export default function EditHistoryPanel() {
  const { instanceId, save, canEdit, editMode, savingKeys } = useReportEdits();
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    if (!instanceId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.REPORT_EDIT_SESSIONS(instanceId), {
        credentials: 'include',
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.error) detail = body.error;
        } catch { /* body not JSON */ }
        throw new Error(detail);
      }
      const data = await r.json();
      setSessions(data.sessions || []);
    } catch (e) {
      setError(e.message || 'Tuntematon virhe');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  // Initial load + reload when instance changes
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // For each edit, look up the previous version of the same field (by
  // edited_at). The diff is rendered against that previous text — so each
  // row shows what *changed* in that particular save.
  const previousByEditId = useMemo(() => {
    if (!sessions) return {};
    const byField = new Map();
    for (const s of sessions) {
      for (const ed of s.edits) {
        if (!byField.has(ed.fieldKey)) byField.set(ed.fieldKey, []);
        byField.get(ed.fieldKey).push(ed);
      }
    }
    const result = {};
    byField.forEach(arr => {
      arr.sort((a, b) => new Date(a.editedAt) - new Date(b.editedAt));
      for (let i = 1; i < arr.length; i++) {
        result[arr[i].id] = arr[i - 1].text;
      }
    });
    return result;
  }, [sessions]);

  if (!canEdit) {
    return (
      <Container>
        <Empty>Vain ylläpitäjille.</Empty>
      </Container>
    );
  }

  return (
    <Container>
      <Banner>
        <Subtitle>
          Muutokset on ryhmitelty muokkausistunnoittain.
          {editMode && ' Olet nyt muokkaustilassa — uusi istunto käynnissä.'}
        </Subtitle>
        <RefreshBtn $loading={loading} onClick={load} title="Päivitä">
          <RefreshCw size={14} />
        </RefreshBtn>
      </Banner>

      {error && (
        <ErrorBox>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>Versiohistorian lataus epäonnistui: {error}</div>
        </ErrorBox>
      )}

      <Body>
        {loading && sessions === null && (
          <LoadingBox><Loader2 size={18} /></LoadingBox>
        )}
        {!loading && !error && sessions !== null && sessions.length === 0 && (
          <Empty>Ei muokkauksia vielä.</Empty>
        )}
        {sessions && sessions.map((s, i) => {
          const key = s.sessionId || `legacy:${s.editedBy}:${i}`;
          const isOpen = expanded.has(key);
          return (
            <SessionCard key={key}>
              <SessionHead onClick={() => toggle(key)}>
                <Chevron>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</Chevron>
                <SessionMeta>
                  <Editor>
                    <User size={12} /> {s.editedBy || 'Tuntematon'}
                    {s.isLegacy && <LegacyTag>vanha</LegacyTag>}
                  </Editor>
                  <SessionTime>{formatRange(s.startedAt, s.endedAt)}</SessionTime>
                </SessionMeta>
                <Count>{s.edits.length}</Count>
              </SessionHead>
              {isOpen && (
                <EditList>
                  {s.edits.map(ed => (
                    <EditRow key={ed.id}>
                      <FieldKey>{ed.fieldKey}</FieldKey>
                      <EditText>
                        <DiffText current={ed.text} previous={previousByEditId[ed.id]} />
                      </EditText>
                      <RestoreBtn
                        type="button"
                        disabled={savingKeys.has(ed.fieldKey)}
                        onClick={() => save(ed.fieldKey, ed.text)}
                        title="Tallenna tämä versio uudelleen voimassaolevaksi"
                      >
                        <RotateCcw size={11} /> Palauta tämä versio
                      </RestoreBtn>
                    </EditRow>
                  ))}
                </EditList>
              )}
            </SessionCard>
          );
        })}
      </Body>
    </Container>
  );
}
